import { cn } from "@genuin/ui/utils";
import { useBaseContext } from "@genuin/components/context/base";
import {
  ControlLayer,
  FeedPlayer,
} from "@genuin/components/molecules/feed-player";
import { PlayerProvider } from "@genuin/components/molecules/feed-player/context";
import type { PostDetailsType } from "@genuin/components/react-query/api/feed/schema";
import { useFeedContext } from "@genuin/components/templates/feed/context";
import { useSwiper } from "swiper/react";
import { useCallback } from "react";
import { useGestureOverlayManager } from "@genuin/components/molecules/gestures";
import { ComponentProps } from "react";

type PlayerProps = {
  post: PostDetailsType;
  onReactionStateChange?: (videoId: string, isReacted: boolean) => void;
  onCommunityJoinStatusChange: ComponentProps<
    typeof ControlLayer
  >["onCommunityJoinStatusChange"];
  onGroupJoinStatusChange: ComponentProps<
    typeof ControlLayer
  >["onGroupJoinStatusChange"];
  onGroupSubscriptionChange: ComponentProps<
    typeof ControlLayer
  >["onGroupSubscriptionChange"];
  onCommentCountChange: ComponentProps<
    typeof ControlLayer
  >["onCommentCountChange"];
  index: number;
  isSectioned?: boolean;
  isActive: boolean;
  isNext: boolean;
  isPrev: boolean;
  isVisible: boolean;
};

// TODO: This component is using feed context, which is not ideal. Remove this dep of FeedContext in future.
export function Player({
  post,
  index,
  onCommunityJoinStatusChange,
  onGroupJoinStatusChange,
  onGroupSubscriptionChange,
  onReactionStateChange,
  onCommentCountChange,
  isSectioned,
  isActive,
  isNext,
  isPrev,
  isVisible,
}: PlayerProps) {
  const { showExpandView, toggleExpandView, activeIndex, variant } =
    useFeedContext();
  const { muted } = useBaseContext();
  // const { isActive, isNext, isPrev, isVisible } = useSwiperSlide();
  const swiper = useSwiper();
  const { showGestureOverlay } = useGestureOverlayManager();

  const handleTimeUpdate = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      if (video.duration > 0) {
        const progress = (video.currentTime / video.duration) * 100;

        if (activeIndex === 1 && progress >= 50) {
          showGestureOverlay("PLAY_PAUSE", muted);
        }
      }
    },
    [activeIndex, muted, showGestureOverlay]
  );

  // load player when the post is active or previous/next post is active or the post is visible
  if (isActive || isNext || isPrev || isVisible)
    return (
      <PlayerProvider
        isActive={isActive}
        videoId={post.video.id}
        showExpandView={showExpandView}
        toggleExpandView={toggleExpandView}
        swiper={swiper}
        index={index}
        onPlayerIterationEnd={swiper.slideNext}
      >
        <div
          className={cn(
            "gencl:group gencl:relative gencl:h-full gencl:w-full gencl:overflow-clip",
            {
              "gencl:sm:rounded-xl": !showExpandView,
            }
          )}
        >
          <FeedPlayer
            videoId={post.video.id}
            src={post.video.source}
            adUrl={post.video.adUrl ?? undefined}
            id={"feed-player--" + post.video.id}
            poster={post.video.thumbnail ?? ""}
            className={cn(
              "gencl:bg-secondary-200 gencl:object-cover gencl:w-full"
            )}
            playsInline
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => {
              showGestureOverlay("SWIPE");
            }}
            style={{ height: "inherit" }}
          />
          <ControlLayer
            isActive={isActive}
            postDetails={post}
            isSectioned={isSectioned}
            onCommunityJoinStatusChange={onCommunityJoinStatusChange}
            onGroupJoinStatusChange={onGroupJoinStatusChange}
            onGroupSubscriptionChange={onGroupSubscriptionChange}
            showCloseButton={variant === "expand"}
            onReactionStateChange={onReactionStateChange}
            onCommentCountChange={onCommentCountChange}
            // Applies GPU acceleration to prevent layer flickering on iOS devices during animations
            className="gencl:translate-x-0"
          />
        </div>
      </PlayerProvider>
    );
}
